import { Switch } from "@/components/Switch";
import { SkeletonBlock } from "@/components/podkit/loading/Skeleton";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Text } from "@/components/podkit/typography/Text";
import { useGetOrganizationPolicies, useUpdateOrganizationPolicies } from "@/queries/organization-queries";
import { formatError } from "@/utils/errors";
import type { PartialMessage } from "@bufbuild/protobuf";
import type { OrganizationPolicies } from "gitpod-next-api/gitpod/v1/organization_pb";
import type { FC } from "react";
import { useCallback, useEffect, useState } from "react";

type PortSharingProps = {
    editable?: boolean;
};

export const PortSharing: FC<PortSharingProps> = ({ editable = true }) => {
    const { data: organizationPolicies, isLoading } = useGetOrganizationPolicies();
    const [enabled, setEnabled] = useState(true); // Default to enabled (portSharingDisabled = false)
    const updateOrganizationPolicies = useUpdateOrganizationPolicies();
    const { toast } = useToast();

    useEffect(() => {
        if (organizationPolicies) {
            // portSharingDisabled = true means port sharing is disabled
            // So enabled state is the inverse of portSharingDisabled
            setEnabled(!organizationPolicies.portSharingDisabled);
        }
    }, [organizationPolicies]);

    const handleToggle = useCallback(
        async (newValue: boolean) => {
            const prevState = enabled;
            setEnabled(newValue);

            const params: PartialMessage<OrganizationPolicies> = {
                // Invert the logic: enabled = true means portSharingDisabled = false
                portSharingDisabled: !newValue,
            };

            try {
                await updateOrganizationPolicies.mutateAsync(params);
                toast({
                    title: newValue ? "Port sharing enabled" : "Port sharing disabled",
                });
            } catch (error) {
                console.error(error);
                // Revert UI state on error
                setEnabled(prevState);
                toast({
                    title: "We couldn't save your changes. Sorry about that! Please retry.",
                    description: formatError(error),
                });
            }
        },
        [enabled, updateOrganizationPolicies, toast],
    );

    return (
        <div className="flex flex-col gap-4 rounded-lg border border-border-subtle px-5 py-4">
            <SkeletonBlock ready={!isLoading}>
                <div className="flex gap-4">
                    <div className="flex">
                        <Switch
                            id="port-sharing-switch"
                            state={enabled ? "checked" : "unchecked"}
                            onToggle={handleToggle}
                            disabled={!editable}
                            aria-label="Allow port sharing in environments"
                        />
                    </div>
                    <div className="flex flex-col gap-1">
                        <Text className="text-base font-medium">Allow Port Sharing in Environments</Text>
                        <Text className="text-sm text-content-secondary">
                            Control whether users can share ports from their environments with external access.
                            VS Code Browser and agents are exempt from this policy and will continue to work
                            when disabled.
                        </Text>
                    </div>
                </div>
            </SkeletonBlock>
        </div>
    );
};
